import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { RescomApiService } from '@app/res-common/services/rescom-api.service';
import { DomSanitizer } from '@angular/platform-browser';
import { LoadOptions } from 'devextreme/data';
import { ResolvedData } from 'devextreme/data/custom_store';
import { Observable, filter, lastValueFrom, map } from 'rxjs';

import { CoreModuleName } from '@app/core/models/core-constant.model';
import { EidosConfigService } from '@common/config/eidos-config.service';
import { IEidosModuleConfiguration } from '@common/config/environment.interface';
import { DynamicApiRequestPaginatedParams, DynamicApiResponse, DynamicApiResponsePaginatedModel } from '@common/models/dynamic-api-response.model';
import { CoreFormatService } from '@common/services/core-format.service';
import { EidosBaseApiService } from '@common/services/eidos-base-api.service';
import { EidosLogService } from '@common/services/eidos-log.service';
import { EidosSecurityService } from '@common/services/eidos-security.service';

import { IReservationModuleAPIConfiguration } from '@app/res-common/models/rescom.model';
import { IResApiGetGeoTreeTopicItinerary, ResBkgVisaHealth, ResTopicItinerary, ResVoyageDescription } from '../models/geo-tree.model';
import { IResApiBkgAddTemplateItems, IResApiGetEresCalendarAvailabilityParams, ResBkgDuration, ResBkgProductTypes, ResBkgTemplate, ResBookingEresCalendarAvailability, ResBookingEresSearchCamp, ResTailorMadeParameters } from '../models/res-booking.model';
import { IReservationPriceSetup } from '../models/res-cached-data.models';
import { IResProductsPaymentSchedulerParams, ResProductsPaymentScheduler } from '../models/res-cruise-availability.models';
import { ReservationCruiseLight } from '../models/res-cruise.models';
import { IResApiGetPriceSetupBookingListParams, IReservationApiGetAllocationByOptPkg, IReservationApiGetAllocationByOption, IReservationApiGetCostPerPax, IReservationApiGetCostPerDayChild, IReservationApiGetCostCopy, IReservationApiGetHolidayMarkup, IReservationApiGetOptionAllocation, IReservationApiGetOptionAllocationDetails, IReservationApiGetOptionAvailability, IReservationApiGetOptionCost, IReservationApiGetOptionPrice, IReservationApiGetPriceDayByDay, IReservationApiManageBandCost, IReservationApiManageCostPerDayChild, IReservationApiManageCostCopy, IReservationApiManageHolidayMarkup, IReservationApiManageOptionAllocation, IReservationApiManageOptionAvailability, IReservationApiManageV2OptionCost, IReservationApiManageV2OptionPrice, IReservationApiOptionPrice, ResPriceSetupBookingList, ReservationGetAllocations, ReservationOptionCost, ReservationOptionCostPerDayChild, ReservationOptionCostHolidayMarkup, ReservationCostCopyService, ReservationCostCopyPeriod, ReservationOptionCostPerPax, ReservationOptionPrice, ReservationOptionPriceAllocation, ReservationOptPkgPriceAllocation, ReservationOptionPriceAllocationDetail, ReservationOptionPriceDayByDay, IReservationApiManageSgjCost, IReservationApiGetSgjCostPerPax, ReservationBandCostCopyService, ReservationBandCostCopyPeriod, IReservationApiManageBandCostCopy, IReservationApiManageCostChildCopy, ReservationOptionCostFilters } from '../models/res-option.models';
import { IResApiGetTourBookParams, IResSearchSmartFilter, ResPackage, ResPackageItinerary, ResPackageType, ResPriceSetupExchangeRate, ResSearchFilterSmartItem, ReservationPackageTravelDateDetail, ReservationProductItineraryBaseData, TourBook } from '../models/res-package.model';
import { ResProductService, ReservationCamp } from '../models/res-product.models';
import { IResCampSelectParams, IResDeckplanInfoParams, IResPackageSearchParams, IResProductServiceSelectParams, ResProductCod, ReservationCruiseSuiteDeckplanConfiguration } from '../models/res-search.models';
import { IResVillaAvailabilityGetParams, ResVillaSellingAvailability } from '@app/res-common/models/villa.model';
import { IResExpAvailabilityGetParams, ResExpAvailability, ResBkgExperiencesApiParams, ResExperience } from '@app/res-common/models/res-experience.model';

@Injectable({
  providedIn: 'root'
})
export class ReservationProductPriceApiService extends EidosBaseApiService {
  private get rescomApiService(): RescomApiService {
    return this.injector.get(RescomApiService);
  }


  constructor(
    public configService: EidosConfigService,
    public eidosSecurityService: EidosSecurityService,
    public http: HttpClient,
    public eidosLogService: EidosLogService,
    public domSanitizer: DomSanitizer,
    public coreFormatService: CoreFormatService,
    private injector: Injector) {
    super(coreFormatService, configService, eidosLogService, eidosSecurityService, http);
    this.configService.currentModulesConfig
      .pipe(
        filter(
          (modules: Array<IEidosModuleConfiguration>) => !!modules.find((module) => module.moduleName === CoreModuleName.Reservation)
        )
      )
      .subscribe((modules) => {
        const module = modules.find((module) => module.moduleName === CoreModuleName.Reservation);
        if (module) {
          const reservationModuleAPI = module.API as IReservationModuleAPIConfiguration;
          this.urlAPI = reservationModuleAPI.url;
          this.staticUrlAPI = reservationModuleAPI.staticUrl;
          this.pluginUrlAPI = reservationModuleAPI.pluginUrl;
          this.apiVersion = reservationModuleAPI.version;
          if (reservationModuleAPI.headers) {
            this.headers = {
              ...reservationModuleAPI.headers,
              ...this.headers
            };
          }
        }
      });
  }

  private fillPaginatedParams(params?: any, loadOptions?: LoadOptions) {
    if (!params) params = {};
    if (!loadOptions) return params;

    // Paginazione
    const skip = loadOptions.skip ?? 0;
    const take = loadOptions.take ?? 20;
    params.PageNum = Math.floor(skip / take) + 1;
    params.PageSize = take;

    // Filtri
    if (loadOptions.filter) {
      const filters = Array.isArray(loadOptions.filter[0])
        ? loadOptions.filter
        : [loadOptions.filter];

      filters.forEach((filter: any) => {
        if (Array.isArray(filter)) {
          const [field, _operator, value] = filter;
          params[field] = value;  // Nome più chiaro
        }
      });
    }

    // Ordinamento
    if (loadOptions.sort) {
      const sort = loadOptions.sort as { selector: string; desc: boolean }[];
      params.OrderField = sort[0]?.selector;
      params.OrderDirect = sort[0]?.desc ? 1 : 0;
    }

    return params;
  }

  bkgAddTemplateItems(params: IResApiBkgAddTemplateItems): Observable<any> {

    return this.callDynamicAPI('bkgtemplate/additems', params, 'POST').pipe(
      map<DynamicApiResponse, any>(response => response.body));
  }

  experienceAvailabilityGet(params: IResExpAvailabilityGetParams): Observable<ResExpAvailability> {
    return this.getDynamicAPI('experience/details', params).pipe(
      map<DynamicApiResponse, ResExpAvailability>((response) => {
        const exp: any = Object.assign({}, response.body.DT0[0] ?? {})
        exp.templates = (response.body.DT1 ?? []).filter((d: any) => d.ExperienceID === exp.ExperienceID)
          .map((t: any) => {
            t.items = (response.body.DT2 ?? []).filter((d: any) => d.ExperienceID === t.ExperienceID && d.ExperienceTemplateHdrID === t.ExperienceTemplateHdrID)
            t.prices = (response.body.DT3 ?? []).filter((d: any) => d.ExperienceTemplateHdrID === t.ExperienceTemplateHdrID)
            return t
          })
        return new ResExpAvailability(exp)
      })
    );
  }

  experienceBook(params: ResBkgExperiencesApiParams) {
    return this.postDynamicAPI('experience/bookExpereiences', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    );
  }

  public getAllocationByOption(params?: IReservationApiGetAllocationByOption): Observable<ReservationOptionPriceAllocation[]> {
    return this.callDynamicAPI('optionAllocation/getAllocationByOption', params).pipe(
      map<DynamicApiResponse, Array<ReservationOptionPriceAllocation>>((response) => {
        return response.body.map((p: any) => new ReservationOptionPriceAllocation(p));
      })
    );
  }

  public getAllocationByOptionPackage(params?: IReservationApiGetAllocationByOptPkg): Observable<ReservationOptPkgPriceAllocation[]> {
    return this.callDynamicAPI('optionAllocation/getAllocationByOptPkg', params).pipe(
      map<DynamicApiResponse, Array<ReservationOptPkgPriceAllocation>>((response) => {
        return response.body.map((p: any) => new ReservationOptPkgPriceAllocation(p));
      })
    );
  }

  public getAllocations(params?: IReservationApiGetOptionAllocation): Observable<ReservationGetAllocations[]> {
    return this.callDynamicAPI('optionAllocation/select', params).pipe(
      map<DynamicApiResponse, Array<ReservationGetAllocations>>((response) => {
        return response.body.map((p: any) => new ReservationGetAllocations(p));
      })
    );
  }

  getBkgProductTypes(JSONCompanyID?: number): Observable<ResBkgProductTypes[]> {
    return this.getDynamicAPI('booking/getBkgProductTypes', { JSONCompanyID: JSONCompanyID }).pipe(
      map<DynamicApiResponse, ResBkgProductTypes[]>((response) => {
        return response.body.map((data: any) => new ResBkgProductTypes(data)).filter((r: ResBkgProductTypes) => r.search)
      })
    );
  }

  /**
   * Retrieves a camp product
   *
   * @param {IResCampSelectParams} params
   * @return {*}  {Observable<ReservationCamp>}
   * @memberof ReservationProductPriceApiService
   */
  public getCamp(params: IResCampSelectParams): Observable<ReservationCamp> {
    return this.callDynamicAPI('product/selectCMP', params, 'POST')
      .pipe(
        map<DynamicApiResponse, ReservationCamp>((response) => new ReservationCamp(response.body))
      );
  }

  public getCostPerDayChild(params?: IReservationApiGetCostPerDayChild): Observable<ReservationOptionCostPerDayChild[]> {
    return this.callDynamicAPI('optionCost/getCostPerDayChild', params).pipe(
      map<DynamicApiResponse, Array<ReservationOptionCostPerDayChild>>((response) => {
        return response.body.map((p: any) => new ReservationOptionCostPerDayChild(p));
      })
    );
  }

  public getCostPerDayChildPaginated(params?: IReservationApiGetCostPerDayChild, loadOptions?: any): Observable<{ data: ReservationOptionCostPerDayChild[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);
    return this.callDynamicAPI('optionCost/getCostPerDayChild', params).pipe(
      map<DynamicApiResponse, { data: ReservationOptionCostPerDayChild[], totalCount: number }>((response) => {
        return {
          data: response.body.map((p: any) => new ReservationOptionCostPerDayChild(p)),
          totalCount: response.recordsCount
        };
      })
    );
  }

  public getCostPerPax(params?: IReservationApiGetCostPerPax): Observable<ReservationOptionCostPerPax[]> {
    return this.callDynamicAPI('optionCost/getCostPerPax', params).pipe(
      map<DynamicApiResponse, Array<ReservationOptionCostPerPax>>((response) => {
        return response.body.map((p: any) => new ReservationOptionCostPerPax(p));
      })
    );
  }

  getCostPerPaxPaginated(params?: IReservationApiGetCostPerPax, loadOptions?: any): Promise<ResolvedData<ReservationOptionCostPerPax>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(
      this.callDynamicAPI('optionCost/getCostPerPax', params).pipe(
        map<DynamicApiResponse, ResolvedData<ReservationOptionCostPerPax>>((response: DynamicApiResponse) => {
          return {
            data: response.body.map((o: any) => new ReservationOptionCostPerPax(o)),
            totalCount: response.recordsCount
          };
        })
      )
    ).catch(() => {
      throw 'Data loading errors';
    });
  }

  getDeckplanInfo(payload: IResDeckplanInfoParams) {
    //TODO cache data
    return this.callDynamicAPI('suite/deckplanInfo', payload, 'GET').pipe(
      map<DynamicApiResponse, ReservationCruiseSuiteDeckplanConfiguration>(
        (response) => new ReservationCruiseSuiteDeckplanConfiguration(response.body[0])
      )
    );
  }

  public getDetailsAllocationByOption(params?: IReservationApiGetOptionAllocationDetails): Observable<ReservationOptionPriceAllocationDetail[]> {
    return this.callDynamicAPI('optionAllocation/getAllocationByOptionDetail', params).pipe(
      map<DynamicApiResponse, Array<ReservationOptionPriceAllocationDetail>>((response) => {
        return response.body.map((p: any) => new ReservationOptionPriceAllocationDetail(p));
      })
    );
  }

  getEresCalendarAvailability(params: IResApiGetEresCalendarAvailabilityParams): Observable<ResBookingEresCalendarAvailability> {
    return this.callDynamicAPI('booking/searchEresBkgAvailability', params).pipe(
      map<DynamicApiResponse, ResBookingEresCalendarAvailability>(
        (response) => new ResBookingEresCalendarAvailability(response.body)
      ));
  }

  getEresSearchAvailabilityCamps(): Observable<ResBookingEresSearchCamp[]> {
    return this.callDynamicAPI('camps/searchAvailability').pipe(
      map<DynamicApiResponse, ResBookingEresSearchCamp[]>(
        (response) => response.body.map((data: any) => new ResBookingEresSearchCamp(data))
      )
    );
  }

  getExchangeRate(baseCurrency: string): Observable<ResPriceSetupExchangeRate[]> {
    return this.getDynamicAPI('priceSetup/getExchangeRate', { CurrencyCod: baseCurrency }).pipe(
      map<DynamicApiResponse, ResPriceSetupExchangeRate[]>((response) => {
        return response.body.map((data: any) => new ResPriceSetupExchangeRate(data))
      }))
  }

  getExperienceDetail(experienceID: number): Observable<ResExperience> {
    return this.callDynamicAPI('experience/view', { ExperienceID: experienceID }).pipe(
      map<DynamicApiResponse, ResExperience>((response) => {
        const exp: any = Object.assign({}, response.body.DT0[0] ?? {})
        exp.templates = (response.body.DT3 ?? []).filter((d: any) => d.ExperienceID === exp.ExperienceID)
          .map((t: any) => {
            t.items = (response.body.DT5 ?? []).filter((d: any) => d.ExperienceID === t.ExperienceID && d.ExperienceTemplateHdrID === t.ExperienceTemplateHdrID)
            return t
          })
        return new ResExperience(exp)
      })
    );
  }

  public getGenericBandCostCopy(params?: IReservationApiGetCostCopy): Observable<(ReservationBandCostCopyService|ReservationBandCostCopyPeriod)[]> {
    return this.callDynamicAPI('service/getBandCostCopy', params).pipe(
      map<DynamicApiResponse, (ReservationBandCostCopyService|ReservationBandCostCopyPeriod)[]>((response) => {
        if((params?.ServiceID??0)>0 && (params?.OptionID??0)>0) return response.body.DT1?.map((p: any) => new ReservationBandCostCopyPeriod(p)) ?? [];
        return response.body.DT0?.map((p: any) => new ReservationBandCostCopyService(p)) ?? [];
      })
    );
  }

  public getGenericCostChildCopy(params?: IReservationApiGetCostCopy): Observable<(ReservationBandCostCopyService|ReservationBandCostCopyPeriod)[]> {
    return this.callDynamicAPI('service/getCostChildCopy', params).pipe(
      map<DynamicApiResponse, (ReservationBandCostCopyService|ReservationBandCostCopyPeriod)[]>((response) => {
        if((params?.ServiceID??0)>0 && (params?.OptionID??0)>0) return response.body.DT1?.map((p: any) => new ReservationBandCostCopyPeriod(p)) ?? [];
        return response.body.DT0?.map((p: any) => new ReservationBandCostCopyService(p)) ?? [];
      })
    );
  }

  public getGenericCostCopy(params?: IReservationApiGetCostCopy): Observable<(ReservationCostCopyService|ReservationCostCopyPeriod)[]> {
    return this.callDynamicAPI('service/getCostCopy', params).pipe(
      map<DynamicApiResponse, (ReservationCostCopyService|ReservationCostCopyPeriod)[]>((response) => {
        if((params?.ServiceID??0)>0 && (params?.OptionID??0)>0) return response.body.DT1?.map((p: any) => new ReservationCostCopyPeriod(p)) ?? [];
        return response.body.DT0?.map((p: any) => new ReservationCostCopyService(p)) ?? [];
      })
    );
  }

  public getHolidayMarkup(params?: IReservationApiGetHolidayMarkup): Observable<ReservationOptionCostHolidayMarkup[]> {
    return this.callDynamicAPI('optionCost/getHolidayMarkup', params).pipe(
      map<DynamicApiResponse, Array<ReservationOptionCostHolidayMarkup>>((response) => {
        return response.body.map((p: any) => new ReservationOptionCostHolidayMarkup(p));
      })
    );
  }

  public getHolidayMarkupPaginated(params?: IReservationApiGetHolidayMarkup, loadOptions?: any): Observable<{ data: ReservationOptionCostHolidayMarkup[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);
    return this.callDynamicAPI('optionCost/getHolidayMarkup', params).pipe(
      map<DynamicApiResponse, { data: ReservationOptionCostHolidayMarkup[], totalCount: number }>((response) => {
        return {
          data: response.body.map((p: any) => new ReservationOptionCostHolidayMarkup(p)),
          totalCount: response.recordsCount
        };
      })
    );
  }

  public getOptionAvailability(params?: IReservationApiGetOptionAvailability): Observable<any[]> {
    return this.callDynamicAPI('optionAvailability/select', params).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body.map((p: IReservationApiOptionPrice) => new ReservationOptionPrice(p));
      })
    );
  }

  public getOptionCityTax(params: { ServiceID: number; OptionID: number }): Observable<{ Type?: string; CityTax?: number, PriceSetupID?: number }> {
    return this.callDynamicAPI('optionCost/getCityTax', params).pipe(
      map<DynamicApiResponse, { Type?: string; CityTax?: number, PriceSetupID?: number }>((response) => {
        const row = Array.isArray(response.body) ? response.body[0] : response.body;
        return {
          Type: row?.Type,
          CityTax: row?.CityTax,
          PriceSetupID: row?.PriceSetupID
        };
      })
    );
  }

  public getOptionCost(params?: IReservationApiGetOptionCost): Observable<{data: ReservationOptionCost[], filters?: ReservationOptionCostFilters}> {
    return this.callDynamicAPI('optionCost/select', params).pipe(
      map<DynamicApiResponse, {data: ReservationOptionCost[], filters?: ReservationOptionCostFilters}>((response) => {
        return {data: response.body?.DT0.map((c: any) => new ReservationOptionCost(c))??[], filters: response.body? new ReservationOptionCostFilters(response.body.DT1?.[0]) : undefined};
      })
    );
  }

  public getOptionPrice(params?: IReservationApiGetOptionPrice): Observable<any[]> {
    return this.callDynamicAPI('optionPrice/select', params).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body.map((p: IReservationApiOptionPrice) => new ReservationOptionPrice(p));
      })
    );
  }

  public getOptionServiceTax(params: { ServiceID: number; OptionID: number }): Observable<{ Type?: string; ServiceTax?: number, PriceSetupID?: number }> {
    return this.callDynamicAPI('optionCost/getServiceTax', params).pipe(
      map<DynamicApiResponse, { Type?: string; ServiceTax?: number, PriceSetupID?: number }>((response) => {
        const row = Array.isArray(response.body) ? response.body[0] : response.body;
        return {
          Type: row?.Type,
          ServiceTax: row?.ServiceTax,
          PriceSetupID: row?.PriceSetupID
        };
      })
    );
  }

  public getOptionVAT(params: { ServiceID: number; OptionID: number }): Observable<{ VAT?: number }> {
    return this.callDynamicAPI('optionCost/getVAT', params).pipe(
      map<DynamicApiResponse, { VAT?: number }>((response) => {
        const row = Array.isArray(response.body) ? response.body[0] : response.body;
        return {
          VAT: row?.VAT
        };
      })
    );
  }

  getPackageItinerary(voyageID: number): Observable<ResPackageItinerary> {
    return this.getDynamicAPI('package/getItinerary', { VoyageID: voyageID }).pipe(
      map<DynamicApiResponse, ResPackageItinerary>((response) => {
        const pkgItinerary = new ResPackageItinerary()

        if (Array.isArray(response.body)) {
          pkgItinerary.itineraries = response.body.map((data: any) => new ReservationProductItineraryBaseData(data))
        } else {
          if (response.body.DT0) pkgItinerary.itineraries = response.body.DT0.map((data: any) => new ReservationProductItineraryBaseData(data))
          if (response.body.DT1) pkgItinerary.calendarDate = response.body.DT1.map((data: any) => new ReservationPackageTravelDateDetail(data))
        }

        return pkgItinerary
      }))
  }

  getPackageType(): Observable<ResPackageType[]> {
    return this.getDynamicAPI('package/getPackageType').pipe(
      map<DynamicApiResponse, ResPackageType[]>((response) => {
        return response.body.map((data: any) => new ResPackageType(data))
      }));
  }

  /**
   * Get light details of all voyages
   * Used in:
   * - Suite setup page
   *
   * @param {{ VoyageID?: number, MonthList?: string[], ShipID?: number[] }} filters
   * @param {DynamicApiRequestPaginatedParams} [params=new DynamicApiRequestPaginatedParams(1, 150)]
   * @return {*}  {Observable<DynamicApiResponsePaginatedModel<ReservationCruiseLight>>}
   * @memberof ReservationProductPriceApiService
   */
  getPagedLightVoyages(
    filters: { VoyageID?: number, MonthList?: string[], ShipID?: number[] },
    params: DynamicApiRequestPaginatedParams = new DynamicApiRequestPaginatedParams(1, 150)
  ): Observable<DynamicApiResponsePaginatedModel<ReservationCruiseLight>> {
    return super.getDynamicAPI('cruise/selectDetail', { ...filters, ...params.serialize() }).pipe(
      map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ReservationCruiseLight>>((response) => {
        const paginatedModel = new DynamicApiResponsePaginatedModel<ReservationCruiseLight>(response);
        paginatedModel.content = response.body.map((cruise: any) => new ReservationCruiseLight(cruise));
        return paginatedModel;
      })
    );
  }

  /**
   *
   * Retrieves a package product
   *
   * @param {IResPackageSearchParams} params
   * @return {*}  {Observable<BookingPackage>}
   * @memberof ReservationProductPriceApiService
   */
  public getPaginatedPackage(params: IResPackageSearchParams): Observable<DynamicApiResponsePaginatedModel<ResPackage>> {
    (params.IsRetail = params?.IsRetail ? 'Y' : 'N');

    if (params?.MonthList) {
      if (params?.MonthList?.length === 0) {
        params.MonthList = '';
      } else if (params?.MonthList?.length === 1) {
        params.MonthList = params.MonthList[0];
      } else {
        params.MonthList = params.MonthList.toString();
      }
    }

    return this.callDynamicAPI(
      'product/searchLightPKG',
      params,
      'POST'
    ).pipe(
      map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ResPackage>>(
        (response) => {
          response.body = response.body.map(
            (pkg: any) => new ResPackage(pkg)
          );
          return new DynamicApiResponsePaginatedModel<ResPackage>(response);
        }
      )
    );
  }

  getPriceDayByDayPaginated(params?: IReservationApiGetPriceDayByDay, loadOptions?: any): Promise<ResolvedData<ReservationOptionPriceDayByDay>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(
      this.callDynamicAPI('optionPrice/getPricePerDay', params).pipe(
        map<DynamicApiResponse, ResolvedData<ReservationOptionPriceDayByDay>>((response: DynamicApiResponse) => {
          return {
            data: response.body.map((o: any) => new ReservationOptionPriceDayByDay(o)),
            totalCount: response.recordsCount
          };
        })
      )
    ).catch(() => {
      throw 'Data loading errors';
    });
  }

  getPriceSetupBookingList(params?: IResApiGetPriceSetupBookingListParams): Observable<ResPriceSetupBookingList[]> {
    return this.getDynamicAPI('priceSetup/selectBookingList', params).pipe(
      map<DynamicApiResponse, ResPriceSetupBookingList[]>((response) => {
        return response.body.map((data: any) => new ResPriceSetupBookingList(data))
      })
    );
  }

  getPriceSetups(params?: Partial<{ PriceSetUpID: number, CostSetup: 'Y' | 'N', PageNum: number, PageSize: number }>): Observable<IReservationPriceSetup[]> {
    return this.rescomApiService.getPriceSetups(params);
  }

  /**
   * Retrieves a service product
   *
   * @param {IResProductServiceSelectParams} params
   * @return {*}  {Observable<ResProductService>}
   * @memberof ReservationProductPriceApiService
   */
  public getProductService(params: IResProductServiceSelectParams): Observable<ResProductService> {
    return this.callDynamicAPI('product/selectSRV', params, 'POST')
      .pipe(
        map<DynamicApiResponse, ResProductService>((response) => new ResProductService(response.body))
      );
  }

  /**
   * Get current selected products
   *
   * @param {ResProductType} productCode
   * @param {number[]} [productIDs=[]]
   * @param {*} [filters]
   * @return {*}  {Promise<any[]>}
   * @memberof ResPreBookingUtility
   */
  getProductsPaymentScheduler(params: IResProductsPaymentSchedulerParams) {
    return this.callDynamicAPI('inventory/pyamentScheduler', params, 'GET').pipe(
      map<DynamicApiResponse, Array<ResProductsPaymentScheduler>>((response) =>
        response.body.map((data: any) => new ResProductsPaymentScheduler(data))
      )
    );
  }

  getSearchDurationFilter(companyID?: number): Observable<ResBkgDuration[]> {
    return this.getDynamicAPI('booking/getSearchDurationFilter', { CompanyID: companyID }).pipe(
      map<DynamicApiResponse, ResBkgDuration[]>((response) => {
        return response.body.map((data: any) => new ResBkgDuration(data))
      })
    );
  }

  getSearchFilterToggleFavourite(section: string, item: ResSearchFilterSmartItem, favourite: boolean): Observable<boolean> {
    const params = {
      ProductSearchFiltersID: item.productSearchFiltersID === 0 ? undefined : item.productSearchFiltersID,
      Section: section,
      ID: item.id === 0 ? undefined : item.id,
      Cod: item.cod === '' ? undefined : item.cod,
      ParentKey: item.relatedKey === '' ? undefined : item.relatedKey,
      Value: item.value,
      Status: favourite ? 'A' : 'D'
    }
    return this.callDynamicAPI('search/filterToogleFavourite', params).pipe(
      map<DynamicApiResponse, boolean>((_response) => {
        return true;
      })
    )
  }

  public getSearchProducts(): Observable<ResProductCod[]> {
    return this.callDynamicAPI('productCod/get').pipe(
      map<DynamicApiResponse, ResProductCod[]>((response) => {
        return response.body.map((data: any) => new ResProductCod(data));
      })
    )
  }

  getSearchSmartFilterPage(params: IResSearchSmartFilter) {
    const p = {
      Section: params.section,
      PageNum: params.pageNum,
      SearchText: (params.searchText ?? '') !== '' ? params.searchText : undefined,
      SelectedCodes: (params.selectedCodes ?? '') !== '' ? params.selectedCodes : undefined
    }
    return this.getDynamicAPI('search/addsmartitems', p).pipe(
      map<DynamicApiResponse, ResSearchFilterSmartItem[]>((response) => {
        return response.body.map((item: any) => new ResSearchFilterSmartItem(item));
      })
    );
  }

  public getSgjCostPerPax(params?: IReservationApiGetSgjCostPerPax): Observable<ReservationOptionCostPerPax[]> {
    return this.callDynamicAPI('price/getSGJCostPerPax', params).pipe(
      map<DynamicApiResponse, Array<ReservationOptionCostPerPax>>((response) => {
        return response.body.map((p: any) => new ReservationOptionCostPerPax(p));
      })
    );
  }

  getTailorMadeParameters(agencyCode?: number) {
    return this.callDynamicAPI('tailorMade/parameters', { AgencyCode: agencyCode }, 'GET').pipe(
      map<DynamicApiResponse, ResTailorMadeParameters>((response) => new ResTailorMadeParameters(response.body))
    );
  }

  getTailorMadeParametersNew(agencyCode?: number) {

    return this.callDynamicAPI('tailorMade/parametersNew', { AgencyCode: agencyCode }, 'GET').pipe(
      map<DynamicApiResponse, ResTailorMadeParameters>((response) => {

        let body:any={}
        if(response.body.DT0) {
          body = response.body.DT0[0]
          body.validPriceType = response.body.DT1 ?? []
        } else if( Array.isArray(response.body)) {
          body = response.body[0]
          body.validPriceType = []
        } else {
          body = response.body
          body.validPriceType = []
        }

        return new ResTailorMadeParameters(body)
      })
    );
  }

  getTemplateItems(templateID: number): Observable<ResBkgTemplate> {
    return this.callDynamicAPI('bkgtemplate/getitems', { ItemTemplateID: templateID }).pipe(
      map<DynamicApiResponse, ResBkgTemplate>(
        (response) => {
          const data = (response.body.DT0 ?? [])[0] ?? {}
          data.Items = response.body.DT1 ?? []

          return new ResBkgTemplate(data)
        }
      ));
  }

  getTopicItinerary(params?: IResApiGetGeoTreeTopicItinerary): Observable<Array<ResTopicItinerary>> {
    return this.callDynamicAPI('topic/getTopicItinerary', params).pipe(
      map<DynamicApiResponse, Array<ResTopicItinerary>>((response) => {
        return response.body.map((data: any) => new ResTopicItinerary(data));
      })
    )
  }

  getTourBook(tourBookParams: IResApiGetTourBookParams): Observable<TourBook[]> {
    return this.getDynamicAPI('product/getTourBook', tourBookParams).pipe(
      map<DynamicApiResponse, TourBook[]>((response) => {
        return response.body.map((data: any) => new TourBook(data))
      })
    );
  }

  getVisaHealthBkg(bkgId: number): Observable<Array<ResBkgVisaHealth>> {
    return this.callDynamicAPI('booking/visa-and-health', { BkgID: bkgId }).pipe(
      map<DynamicApiResponse, Array<ResBkgVisaHealth>>((response) => {
        if (!response.body.DT0) return []
        return response.body.DT0.map((data: any) => new ResBkgVisaHealth(data));
      })
    )
  }

  getVoyageDescription(voyageID?: number): Observable<ResVoyageDescription> {
    return this.callDynamicAPI('bkg/getVoyageEcmDesc', { voyageID: voyageID }).pipe(
      map<DynamicApiResponse, ResVoyageDescription>((response) => {
        return new ResVoyageDescription(response.body[0])
      })
    )
  }

  public manageBandCost(params: IReservationApiManageBandCost): Observable<any> {
    return this.postDynamicAPI('optionCost/manageBandCost', params);
  }

  public manageBandCostCopy(params: IReservationApiManageBandCostCopy): Observable<any> {
    return this.postDynamicAPI('service/manageBandCostCopy', params);
  }

  public manageCostChildCopy(params: IReservationApiManageCostChildCopy): Observable<any> {
    return this.postDynamicAPI('service/manageCostChildCopy', params);
  }

  public manageCostCopy(params: IReservationApiManageCostCopy): Observable<any> {
    return this.postDynamicAPI('service/manageCostCopy', params);
  }

  public manageCostPerDayChild(params: IReservationApiManageCostPerDayChild): Observable<any> {
    return this.postDynamicAPI('optionCost/manageCostPerDayChild', params);
  }

  public manageHolidayMarkup(params: IReservationApiManageHolidayMarkup): Observable<any> {
    return this.postDynamicAPI('optionCost/manageHolidayMarkup', params);
  }

  public manageOptionAllocation(params: IReservationApiManageOptionAllocation): Observable<any> {
    return this.postDynamicAPI('optionAllocation/manage', params);
  }

  public manageOptionAvailability(params: IReservationApiManageOptionAvailability): Observable<any> {
    return this.postDynamicAPI('optionAvailability/manage', params);
  }

  public manageOptionCost(params: IReservationApiManageV2OptionCost): Observable<any> {
    return this.postDynamicAPI('optionCost/manageV2', params);
  }

  public manageOptionPriceAllPt(params: IReservationApiManageV2OptionPrice): Observable<any> {
    return this.postDynamicAPI('optionPrice/manageAllPt', params);
  }

  public manageSgjCost(params: IReservationApiManageSgjCost): Observable<any> {
    return this.postDynamicAPI('price/manageSGJCostPerPax', params);
  }

  villaSellingAvailabilityGet(params: IResVillaAvailabilityGetParams) {
    return this.callDynamicAPI('product/villaAvailability', params).pipe(
      map<DynamicApiResponse, ResVillaSellingAvailability>((response) => {
        const villa: any = response.body.DT0 && response.body.DT0.length > 0 ? response.body.DT0[0] : {};
        villa.Availability = response.body.DT1 ? response.body.DT1 : []
        villa.Prices = response.body.DT2 ? response.body.DT2 : []
        villa.EcmData = response.body.DT3 ? response.body.DT3 : []
        villa.Amenities = response.body.DT4 ? response.body.DT4 : []
        villa.Options = response.body.DT5 ? response.body.DT5 : []
        villa.Bailment = response.body.DT6 && response.body.DT6.length > 0 ? response.body.DT6[0] : {};
        return new ResVillaSellingAvailability(villa)
      })
    )
  }
}
